import {
  ChangeDetectionStrategy,
  Component,
  Input,
  effect,
  inject,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { QuestionTitlePipe } from '../question-title.pipe';
import { DiagramComponent } from '../diagram/diagram.component';
import { ContactCircleComponent } from '../contact-circle/contact-circle.component';
import { ContactTooltipComponent } from 'src/app/shared/components/contact-tooltip/contact-tooltip.component';
import { RecipientsDisplayComponent } from 'src/app/shared/components/recipients-display/recipients-display.component';

import { DataSummaryStore } from 'src/app/features/questionnaires/store/data-summary-store';
import { OptionData, QuestionData } from 'src/app/models/QuestionnaireDataByQuestions.model';

@Component({
    selector: 'que-answers-in-diagram',
    imports: [
        CommonModule,
        QuestionTitlePipe,
        DiagramComponent,
        TranslateDirective, TranslatePipe,
        ContactCircleComponent,
        ContactTooltipComponent,
        RecipientsDisplayComponent
    ],
    templateUrl: './answers-in-diagram.component.html',
    styleUrls: ['./answers-in-diagram.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AnswersInDiagramComponent {
  #storeData = inject(DataSummaryStore);

  // store signals
  readonly recipientsToFilterUserId$ = toSignal(this.#storeData.recipientsToFilterId$); // userId (יכול להיות undefined)
  readonly recipientIdToFilter$ = toSignal(this.#storeData.recipientIdToFilter$);       // recipientId (יכול להיות null/undefined)
  readonly showComparison$ = toSignal(this.#storeData.showComparison$);

  // input as signal for reliable reactivity
  private readonly _questionData = signal<QuestionData | undefined>(undefined);

  @Input() set questionData(value: QuestionData | undefined) {
    this._questionData.set(value);
  }
  get questionData(): QuestionData | undefined {
    return this._questionData();
  }

  options: OptionData[] = [];
  showAll = false;

  constructor() {
    effect(() => {
      // dependencies
      this._questionData();
      this.recipientsToFilterUserId$();
      this.recipientIdToFilter$();
      this.showComparison$();

      // recompute
      this.buildOptions();
    });
  }

  showHide() {
    this.showAll = !this.showAll;
  }

  // ---------------- core ----------------

  private buildOptions(): void {
    const qd = this._questionData();
    if (!qd) {
      this.options = [];
      return;
    }

    const userId = this.recipientsToFilterUserId$();   // userId filter
    const recipientId = this.recipientIdToFilter$();   // submission filter

    // בסיס אחוזים = מספר ההגשות של המשתמש (אם נבחר userId), אחרת מספר כלל ההגשות
    // כאן אנחנו משתמשים ב-questionData.recipients (מבנה: {recipient:{id,user}, answer})
    const base = this.filterQuestionRecipients(qd.recipients ?? [], userId, null);
    const totalBase = base.length;

    this.options = (qd.options ?? []).map(op => {
      const curr = this.filterOptionRecipients(op.recipients ?? [], userId, recipientId);
      const prev = this.filterOptionRecipients(op.recipientsSource ?? [], userId, recipientId);

      const percent =
        recipientId != null
          ? (curr.length > 0 ? 100 : 0)
          : (totalBase ? Math.round((curr.length / totalBase) * 100) : 0);

      const percentSource =
        recipientId != null
          ? (prev.length > 0 ? 100 : 0)
          : (totalBase ? Math.round((prev.length / totalBase) * 100) : 0);

      return {
        ...op,
        percent,
        percentSource,
        wasChange: percent !== percentSource
      } as OptionData;
    });
  }

  // recipients ברמת השאלה: { recipient: { id, user }, answer }
  private filterQuestionRecipients(list: any[], userId?: number, recipientId?: number | null): any[] {
    let res = list ?? [];

    if (userId != null) {
      res = res.filter(r => r?.recipient?.user?.id === userId);
    }

    if (recipientId != null) {
      res = res.filter(r => r?.recipient?.id === recipientId);
    }

    return res;
  }

  // recipients ברמת option: לרוב Recipient עם user + id (recipientId)
  private filterOptionRecipients(list: any[], userId?: number, recipientId?: number | null): any[] {
    let res = list ?? [];

    // 1) תמיד קודם לפי userId
    if (userId != null) {
      res = res.filter(r => (r?.user?.id ?? r?.recipient?.user?.id) === userId);
    }

    // 2) ואז לפי submission recipientId אם נבחר
    if (recipientId != null) {
      res = res.filter(r => this.getRecipientId(r) === recipientId);
    }

    return res;
  }

  private getRecipientId(r: any): number | undefined {
    // ברוב המקרים אצלך: r.id הוא recipientId
    return r?.id;

    // אם אצלך זה עטוף, החליפי ל:
    // return r?.recipient?.id;
  }

  // helpers to keep template readable
  get hasUserFilter(): boolean {
    return this.recipientsToFilterUserId$() != null;
  }

  get hasSubmissionFilter(): boolean {
    return this.recipientIdToFilter$() != null;
  }

  shouldShowOption(optionData: OptionData, index: number): boolean {
    if (!(this.showAll || index < 5)) return false;

    // בלי פילטר userId – מציגים הכל
    if (!this.hasUserFilter) return true;

    // עם פילטר userId – מציגים רק מה שרלוונטי (שלא 0)
    if (optionData.percent !== 0) return true;
    if (this.showComparison$() && optionData.percentSource !== 0) return true;

    return false;
  }
}
